"use client";

import React from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import {
  SidebarProvider,
  Sidebar,
  SidebarHeader,
  SidebarContent,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
  SidebarInset,
  SidebarFooter,
  SidebarTrigger,
  SidebarPinTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { ChevronRight, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AppLogo } from "./icons";
import {
  LayoutDashboard,
  List,
  Users,
  Settings,
  MapPin,
  Building,
  Landmark,
  User as UserIcon,
  Drama,
  LifeBuoy,
  MessageCircle,
  Shield,
  FileSpreadsheet,
} from "lucide-react";
import { useAuth } from "@/context/auth-context";
import { useKeyboardShortcuts } from "@/hooks/use-keyboard-shortcuts";
import { ThemeSwitcher } from "./theme-switcher";
import { UserProfileMenu } from "./user-profile-menu";
import { UpdatesMenu } from "./bottom-left-corner/updates-menu";
import { HelpMenu } from "./bottom-left-corner/help-menu";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { Label } from "./ui/label";
import { cn } from "@/lib/utils";
import deals from "@/lib/placeholder-deals.json";
import { Badge } from "./ui/badge";
import { Switch } from "./ui/switch";
import { doc, onSnapshot } from "firebase/firestore";
import { getV3Db } from "@/lib/v3/db";
import { ReferralBanner } from "./referral-banner";
import { StripePaymentIssueBanner } from "./stripe-payment-issue-banner";
import { TrainingZonePanel } from "./training-zone-panel";
import {
  getMasqueradeUserDirectory,
  type MasqueradeUserDirectoryRow,
} from "@/app/admin-actions";
import { Combobox } from "@/components/ui/combobox";
import type { AppUser } from "@/lib/types";
import {
  deployConsoleLinks,
  formatDeployInstant,
  getDeployInfo,
  githubCommitUrl,
} from "@/lib/deploy-info";

function hasMasqueradeGameData(r: MasqueradeUserDirectoryRow): boolean {
  return r.v3GameCount > 0;
}

function AdminPanelDeployFooter() {
  const d = getDeployInfo();
  const hasBuildId = d.cloudBuildId.length > 0;
  const commitHref = githubCommitUrl(d.commitSha);

  return (
    <div
      className={cn(
        "border-t border-sidebar-border mx-2 mt-1 mb-2 px-2 py-2",
        "text-[10px] text-muted-foreground space-y-1.5",
        "group-data-[collapsible=icon]:hidden",
      )}
    >
      <p className="leading-tight">
        <span className="text-sidebar-foreground/80">Commit </span>
        {commitHref ? (
          <a
            href={commitHref}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-0.5 font-mono text-primary underline underline-offset-2 hover:text-primary/90"
            title={d.commitSha}
          >
            {d.commitShort}
            <ExternalLink className="h-3 w-3 shrink-0" aria-hidden />
          </a>
        ) : (
          <span
            className="font-mono text-sidebar-foreground/90"
            title={d.commitSha}
          >
            {d.commitShort}
          </span>
        )}
      </p>
      {d.commitIso ? (
        <p className="leading-tight">
          <span className="text-sidebar-foreground/80">Committed </span>
          {formatDeployInstant(d.commitIso)}
        </p>
      ) : null}
      {d.bundleAtIso ? (
        <p className="leading-tight">
          <span className="text-sidebar-foreground/80">Bundle built </span>
          {formatDeployInstant(d.bundleAtIso)}
          <span className="block text-muted-foreground/90 mt-0.5">
            Image build time, not exact rollout finish.
          </span>
        </p>
      ) : null}
      {hasBuildId ? (
        <p className="leading-tight font-mono break-all">
          <span className="text-sidebar-foreground/80">Cloud Build </span>
          <a
            href={deployConsoleLinks.cloudBuildDetail(d.cloudBuildId)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-0.5 text-primary underline underline-offset-2 hover:text-primary/90"
          >
            {d.cloudBuildId}
            <ExternalLink className="h-3 w-3 shrink-0" aria-hidden />
          </a>
        </p>
      ) : null}
      <div className="flex flex-wrap gap-x-2 gap-y-1 pt-0.5">
        <a
          href={deployConsoleLinks.firebaseAppHosting}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-0.5 text-primary underline underline-offset-2 hover:text-primary/90"
        >
          App Hosting
          <ExternalLink className="h-3 w-3 shrink-0" aria-hidden />
        </a>
        <a
          href={deployConsoleLinks.appHostingOverview}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-0.5 text-primary underline underline-offset-2 hover:text-primary/90"
        >
          Overview
          <ExternalLink className="h-3 w-3 shrink-0" aria-hidden />
        </a>
        <a
          href={deployConsoleLinks.appHostingRollouts}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-0.5 text-primary underline underline-offset-2 hover:text-primary/90"
        >
          Rollouts
          <ExternalLink className="h-3 w-3 shrink-0" aria-hidden />
        </a>
        <a
          href={deployConsoleLinks.appHostingLogs}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-0.5 text-primary underline underline-offset-2 hover:text-primary/90"
        >
          Logs
          <ExternalLink className="h-3 w-3 shrink-0" aria-hidden />
        </a>
        <a
          href={deployConsoleLinks.appHostingUsage}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-0.5 text-primary underline underline-offset-2 hover:text-primary/90"
        >
          Usage
          <ExternalLink className="h-3 w-3 shrink-0" aria-hidden />
        </a>
      </div>
    </div>
  );
}

function masqueradePickerLabel(
  r: MasqueradeUserDirectoryRow,
  trueAdminUid: string | undefined,
): string {
  const you = r.uid === trueAdminUid ? " [you]" : "";
  const identity =
    r.displayName?.trim() && r.email
      ? `${r.displayName.trim()} (${r.email})`
      : r.email || r.displayName?.trim() || r.uid;
  return `${identity} · games: ${r.v3GameCount}${you}`;
}

const allMenuItems = [
  { href: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { href: "/games", label: "Games", icon: List },
  { href: "/locations", label: "Locations", icon: MapPin },
  { href: "/people", label: "People", icon: Users },
  { href: "/organizations", label: "Organizations", icon: Building },
  { href: "/teams", label: "Teams", icon: Shield },
  { href: "/finances", label: "Finances", icon: Landmark },
  { href: "/data", label: "Spreadsheet", icon: FileSpreadsheet },
  { href: "/account", label: "Account", icon: UserIcon },
  { href: "/settings", label: "Settings", icon: Settings },
  { href: "/support", label: "Get Help", icon: LifeBuoy },
  {
    href: "/admin",
    label: "Admin Panel",
    icon: Drama,
    admin: true,
  },
];

function isMainNavItemActive(pathname: string | null, href: string): boolean {
  if (!pathname) return false;
  if (href === "/") return pathname === "/";
  return pathname === href || pathname.startsWith(`${href}/`);
}

type AdminSubrouteLink = {
  href: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
};
type AdminSubroute = AdminSubrouteLink;

function MasqueradeBanner() {
  const {
    user,
    getFirebaseIdToken,
    isMasquerading,
    stopMasquerading,
    masqueradeAs,
    masqueradeUser,
    isAdmin,
    trueAdminUser,
    enforceMasqueradeSubscription,
    setEnforceMasqueradeSubscription,
  } = useAuth();

  const [directoryRows, setDirectoryRows] = React.useState<
    MasqueradeUserDirectoryRow[]
  >([]);
  const [directoryLoading, setDirectoryLoading] = React.useState(false);
  const [directoryError, setDirectoryError] = React.useState<string | null>(
    null,
  );

  React.useEffect(() => {
    if (!isMasquerading || !isAdmin || !user) return;
    let cancelled = false;
    setDirectoryLoading(true);
    setDirectoryError(null);
    void getFirebaseIdToken()
      .then((idToken) => getMasqueradeUserDirectory(idToken))
      .then((rows) => {
        if (!cancelled) setDirectoryRows(rows);
      })
      .catch((err: unknown) => {
        console.error("[MasqueradeBanner] getMasqueradeUserDirectory:", err);
        if (!cancelled) {
          setDirectoryError(
            err instanceof Error ? err.message : "Failed to load users",
          );
        }
      })
      .finally(() => {
        if (!cancelled) setDirectoryLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isMasquerading, isAdmin, user, getFirebaseIdToken]);

  const optionGroups = React.useMemo(() => {
    if (directoryRows.length === 0) return [];
    const withData = directoryRows.filter(hasMasqueradeGameData);
    const without = directoryRows.filter((r) => !hasMasqueradeGameData(r));
    const mapRow = (r: MasqueradeUserDirectoryRow) => ({
      value: r.uid,
      label: masqueradePickerLabel(r, trueAdminUser?.uid),
    });
    const groups: {
      heading: string;
      options: { label: string; value: string }[];
    }[] = [];
    if (withData.length > 0) {
      groups.push({
        heading: "Users with games (v3db)",
        options: withData.map(mapRow),
      });
    }
    if (without.length > 0) {
      groups.push({
        heading: "No games in v3db",
        options: without.map(mapRow),
      });
    }
    return groups;
  }, [directoryRows, trueAdminUser?.uid]);

  const handleUidChange = React.useCallback(
    (uid: string) => {
      if (!uid) return;
      if (uid === trueAdminUser?.uid) {
        stopMasquerading();
        return;
      }
      const row = directoryRows.find((r) => r.uid === uid);
      if (row) {
        masqueradeAs({
          uid: row.uid,
          email: row.email,
          displayName: row.displayName,
          photoURL: row.photoURL,
        });
      }
    },
    [trueAdminUser?.uid, directoryRows, masqueradeAs, stopMasquerading],
  );

  if (!isMasquerading || !masqueradeUser) {
    return null;
  }

  return (
    <div className="bg-yellow-400 text-yellow-900 text-sm font-semibold p-2 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 w-full">
      <div className="flex items-center gap-2 min-w-0 flex-1 justify-center sm:justify-start sm:flex-initial">
        <Drama className="h-5 w-5 shrink-0" />
        {isAdmin ? (
          <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 min-w-0 flex-1 max-w-xl">
            <span className="shrink-0 whitespace-nowrap">Masquerading as</span>
            {directoryLoading ? (
              <span className="text-xs font-medium opacity-90 truncate">
                Loading users…
              </span>
            ) : directoryError ? (
              <span className="truncate font-bold" title={directoryError}>
                {masqueradeUser.email ?? masqueradeUser.uid}
              </span>
            ) : (
              <Combobox
                optionGroups={optionGroups}
                value={masqueradeUser.uid}
                onChange={handleUidChange}
                placeholder="Select user…"
                searchPlaceholder="Search name or email…"
                emptyMessage="No user matches."
                triggerClassName="h-8 text-sm bg-yellow-50 border-yellow-900 text-yellow-950 hover:bg-yellow-100"
                contentClassName="min-w-[var(--radix-popover-trigger-width)] max-w-lg w-[min(100vw-2rem,36rem)]"
              />
            )}
          </div>
        ) : (
          <span className="truncate">
            Masquerading as{" "}
            <span className="font-bold">{masqueradeUser.email}</span>
          </span>
        )}
      </div>
      {isAdmin && (
        <label className="flex items-center gap-2 cursor-pointer font-medium text-yellow-950">
          <Switch
            checked={enforceMasqueradeSubscription}
            onCheckedChange={setEnforceMasqueradeSubscription}
            className="data-[state=checked]:bg-yellow-800"
            aria-label="Use target account subscription for paywall and subscribe flow"
          />
          <span className="whitespace-nowrap">Target subscription</span>
        </label>
      )}
      <Button
        size="sm"
        variant="secondary"
        className="h-7"
        onClick={stopMasquerading}
      >
        Exit Masquerade
      </Button>
    </div>
  );
}

function AppShellContent({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const { user, uiMode, setUiMode, isAdmin, getFirebaseIdToken } = useAuth();
  const { setOpenMobile, state: sidebarState } = useSidebar();
  const [adminPanelOpen, setAdminPanelOpen] = React.useState(true);
  const [trainingZoneOpen, setTrainingZoneOpen] = React.useState(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem("tz-open") === "1";
  });
  const [selectedDocSlug, setSelectedDocSlug] = React.useState(() => {
    if (typeof window === "undefined") return "/docs/getting-started";
    return window.localStorage.getItem("tz-slug") || "/docs/getting-started";
  });
  React.useEffect(() => {
    window.localStorage.setItem("tz-open", trainingZoneOpen ? "1" : "0");
  }, [trainingZoneOpen]);
  React.useEffect(() => {
    window.localStorage.setItem("tz-slug", selectedDocSlug);
  }, [selectedDocSlug]);
  React.useEffect(() => {
    if (pathname?.startsWith("/docs")) {
      setTrainingZoneOpen(true);
      setSelectedDocSlug(pathname);
    }
  }, [pathname]);
  const adminSubroutes: AdminSubroute[] = [
    { href: "/admin", label: "Admin Tools", icon: Drama },
    { href: "/admin/user-dashboard", label: "User Dashboard", icon: Users },
    {
      href: "/admin/support-requests",
      label: "Support Requests",
      icon: MessageCircle,
    },
  ];

  // Enable keyboard shortcuts
  useKeyboardShortcuts();
  const [dealOfTheDayId, setDealOfTheDayId] = React.useState<
    string | number | null
  >(deals[0]?.id ?? null);
  const [referralBannerEnabled, setReferralBannerEnabled] =
    React.useState<boolean>(false);

  React.useEffect(() => {
    const settingsDocRef = doc(getV3Db(), "settings", "siteSettings");
    const unsubscribe = onSnapshot(settingsDocRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        setDealOfTheDayId(data.dealOfTheDayId ?? null);
        setReferralBannerEnabled(data.referralBannerEnabled === true);
      }
    });
    return () => unsubscribe();
  }, []);

  const dealOfTheDay =
    deals.find((d) => String(d.id) === String(dealOfTheDayId)) || deals[0];

  const menuItems = React.useMemo(() => {
    let items = allMenuItems;
    if (!isAdmin) {
      items = items.filter((item) => !item.admin);
    }
    return items.map((item) => {
      if (item.href === "/deals") {
        return { ...item, teaser: dealOfTheDay?.teaser };
      }
      return item;
    });
  }, [dealOfTheDay, isAdmin]);

  if (!user) {
    return <>{children}</>;
  }

  return (
    <>
      <Sidebar collapsible="icon">
        <SidebarHeader className="p-0 gap-0">
          {/* Row 1: OfficialsPay logo — collapse trigger left, logo text center */}
          <div className="flex w-full items-center bg-primary group-data-[collapsible=icon]:justify-center">
            <SidebarTrigger
              className={cn(
                "hidden md:inline-flex shrink-0",
                "h-8 w-8 rounded-sm",
                "text-primary-foreground/70 hover:text-primary-foreground",
                "hover:bg-primary-foreground/10",
              )}
            />
            <div className="flex-1 flex items-center justify-center py-2 group-data-[collapsible=icon]:hidden">
              <Link
                href="/data"
                className="font-bold text-lg tracking-tight text-primary-foreground"
              >
                OfficialsPay
                {uiMode === "multi-sport" && (
                  <span className="text-xs font-normal opacity-80 ml-1">
                    Multi-Sport
                  </span>
                )}
              </Link>
            </div>
          </div>
          {/* Row 2: Training Zone — pin icon left, label, chevron right */}
          <div
            className={cn(
              "flex w-full items-center",
              "bg-yellow-400/20 text-yellow-700 dark:bg-yellow-500/20 dark:text-yellow-300",
              "group-data-[collapsible=icon]:justify-center",
            )}
          >
            <SidebarPinTrigger
              className={cn(
                "hidden md:inline-flex shrink-0",
                "h-8 w-8 rounded-sm",
                "text-yellow-700/60 dark:text-yellow-300/60",
                "hover:text-yellow-700 dark:hover:text-yellow-300",
                "hover:bg-yellow-400/30 dark:hover:bg-yellow-500/30",
              )}
            />
            <button
              onClick={() => {
                setTrainingZoneOpen((prev) => !prev);
                setOpenMobile(false);
              }}
              className={cn(
                "flex flex-1 items-center gap-2 px-2 py-2 text-left",
                "font-semibold hover:bg-yellow-400/30 dark:hover:bg-yellow-500/30",
                "group-data-[collapsible=icon]:hidden",
              )}
            >
              <span className="flex-1 truncate text-sm">Training Zone</span>
              <ChevronRight
                className={cn(
                  "h-4 w-4 transition-transform shrink-0",
                  trainingZoneOpen && "rotate-90",
                )}
              />
            </button>
          </div>
        </SidebarHeader>
        <SidebarContent>
          <SidebarMenu>
            {menuItems.map((item) => {
              // Admin Panel: expanded submenu for all admins (User Dashboard, Support Requests, Data Explorer, etc.)
              if (item.href === "/admin" && isAdmin) {
                return (
                  <React.Fragment key={item.href}>
                    <SidebarMenuItem>
                      <Collapsible
                        open={adminPanelOpen}
                        onOpenChange={setAdminPanelOpen}
                      >
                        <CollapsibleTrigger asChild>
                          <SidebarMenuButton
                            isActive={pathname.startsWith("/admin")}
                            onClick={() => setOpenMobile(false)}
                            title={
                              sidebarState === "collapsed"
                                ? item.label
                                : undefined
                            }
                          >
                            <item.icon />
                            <span className="flex-1">{item.label}</span>
                            <ChevronRight
                              className={cn(
                                "h-4 w-4 transition-transform",
                                adminPanelOpen && "rotate-90",
                              )}
                            />
                          </SidebarMenuButton>
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <SidebarMenuSub>
                            {adminSubroutes.map((sub) => (
                              <SidebarMenuSubItem key={sub.href}>
                                <SidebarMenuSubButton
                                  asChild
                                  isActive={
                                    pathname === sub.href ||
                                    (sub.href !== "/admin" &&
                                      pathname.startsWith(sub.href))
                                  }
                                >
                                  <Link
                                    href={sub.href}
                                    title=""
                                    onClick={() => setOpenMobile(false)}
                                  >
                                    <sub.icon className="h-4 w-4" />
                                    <span>{sub.label}</span>
                                  </Link>
                                </SidebarMenuSubButton>
                              </SidebarMenuSubItem>
                            ))}
                          </SidebarMenuSub>
                          <AdminPanelDeployFooter />
                        </CollapsibleContent>
                      </Collapsible>
                    </SidebarMenuItem>
                  </React.Fragment>
                );
              }

              // Regular menu items
              return (
                <SidebarMenuItem key={item.href}>
                  <SidebarMenuButton
                    asChild
                    isActive={isMainNavItemActive(pathname, item.href)}
                    onClick={() => setOpenMobile(false)}
                    data-menu-href={item.href}
                  >
                    <Link
                      href={item.href}
                      aria-label={
                        sidebarState === "collapsed" ? item.label : undefined
                      }
                      title={
                        sidebarState === "collapsed" ? item.label : undefined
                      }
                    >
                      <item.icon />
                      <span
                        className={cn(
                          "flex-1",
                          sidebarState === "collapsed"
                            ? "sr-only"
                            : "truncate",
                        )}
                      >
                        {item.label}
                      </span>
                      {"teaser" in item && item.teaser && (
                        <Badge
                          variant="secondary"
                          className="ml-2 bg-accent/80 text-accent-foreground group-data-[collapsible=icon]:hidden"
                        >
                          {item.teaser}
                        </Badge>
                      )}
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              );
            })}
          </SidebarMenu>
        </SidebarContent>
        <SidebarFooter className="p-0 gap-0">
          {/* Sport mode toggle */}
          <div className="px-2 pt-1 pb-1 group-data-[collapsible=icon]:hidden">
            <RadioGroup
              value={uiMode}
              onValueChange={(value: "baseball" | "multi-sport") =>
                setUiMode(value)
              }
              className="flex rounded-lg border p-1"
            >
              <RadioGroupItem
                value="baseball"
                id="sidebar-baseball-mode"
                className="sr-only"
              />
              <Label
                htmlFor="sidebar-baseball-mode"
                className={cn(
                  "px-2 py-1.5 rounded-md cursor-pointer text-xs w-full text-center",
                  uiMode === "baseball" &&
                    "bg-sidebar-accent text-sidebar-accent-foreground font-semibold",
                )}
              >
                Baseball
              </Label>

              <RadioGroupItem
                value="multi-sport"
                id="sidebar-multisport-mode"
                className="sr-only"
              />
              <Label
                htmlFor="sidebar-multisport-mode"
                className={cn(
                  "px-2 py-1.5 rounded-md cursor-pointer text-xs w-full text-center",
                  uiMode === "multi-sport" &&
                    "bg-sidebar-accent text-sidebar-accent-foreground font-semibold",
                )}
              >
                Multi-Sport
              </Label>
            </RadioGroup>
          </div>

          {/* Utility row: theme, updates, help, user */}
          <div className="flex items-center justify-between w-full px-2 py-1.5 group-data-[collapsible=icon]:flex-col group-data-[collapsible=icon]:gap-2 group-data-[collapsible=icon]:px-0">
            <div className="flex items-center gap-0.5 group-data-[collapsible=icon]:flex-col-reverse group-data-[collapsible=icon]:gap-2">
              <ThemeSwitcher />
              <UpdatesMenu />
              <HelpMenu />
            </div>
            <UserProfileMenu />
          </div>
        </SidebarFooter>
      </Sidebar>
      <TrainingZonePanel
        open={trainingZoneOpen}
        onClose={() => setTrainingZoneOpen(false)}
        selectedDoc={selectedDocSlug}
        onSelectDoc={setSelectedDocSlug}
      />
      <SidebarInset>
        <MasqueradeBanner />
        <header className="sticky top-0 z-10 flex h-14 items-center gap-2 border-b bg-background px-2 sm:static sm:h-auto sm:border-0 sm:bg-transparent sm:px-6">
          <SidebarTrigger className="sm:hidden" />
          <div className="md:hidden">
            <Link href="/">
              <AppLogo />
            </Link>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex md:hidden items-center gap-0">
              {menuItems.map((item) => (
                <Button
                  key={item.href}
                  asChild
                  variant={
                    isMainNavItemActive(pathname, item.href)
                      ? "secondary"
                      : "ghost"
                  }
                  size="icon"
                >
                  <Link href={item.href} aria-label={item.label} title={item.label}>
                    <item.icon />
                    <span className="sr-only">{item.label}</span>
                  </Link>
                </Button>
              ))}
            </div>
          </div>
        </header>
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden p-4 sm:p-6 lg:p-8">
          <div
            className={cn(
              "grid shrink-0 transition-[grid-template-rows] duration-300 ease-in-out",
              referralBannerEnabled ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
            )}
          >
            <div className="overflow-hidden">
              {referralBannerEnabled && <ReferralBanner />}
            </div>
          </div>
          <StripePaymentIssueBanner />
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            {children}
          </div>
        </div>
      </SidebarInset>
    </>
  );
}

export function AppShell({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <AppLogo iconSize="size-10" textSize="text-2xl" />
      </div>
    );
  }

  if (!user) {
    return <>{children}</>;
  }

  return (
    <SidebarProvider>
      <AppShellContent>{children}</AppShellContent>
    </SidebarProvider>
  );
}
